// Skrivfältet i chattarna (backlogg C1, förlaga docs/backlog-bilder/c1-skrivfalt-whatsapp.png):
// "+" för bilagor, rundat fält med emojiknapp inuti och en rund mikrofonknapp som blir skicka när det finns text.
import React from 'react';
import { Mic, Plus, SendHorizontal, Smile, Square, X } from 'lucide-react';
import { RADIUS, TAP, useTheme } from '../../theme';
import { Icon, IconButton, Menu, type MenuItem } from '../ui';
import { dictationSupported, useDictation, type DictationState } from './useDictation';

/** Vanliga emoji i jaktlaget. Hela tangentbordets emoji finns kvar på mobilen. */
export const CHAT_EMOJI = ['👍', '👌', '🙏', '😀', '😂', '😅', '😊', '😍', '🤔', '😮', '😬', '😢', '❤️', '🔥', '💪', '👀', '🎯', '✅', '❌', '📍', '☕', '🌲', '🦌', '🐗', '🦊', '🐕', '🌧️', '☀️'];

/** Vad den runda knappen gör: skicka när det finns text, annars diktera (eller stoppa inspelningen). */
export function composerAction(value: string, canDictate: boolean, state: DictationState): 'send' | 'mic' | 'stop' | 'busy' {
  if (state === 'recording') return 'stop';
  if (state === 'transcribing') return 'busy';
  if (value.trim() || !canDictate) return 'send';
  return 'mic';
}

/** Sätter in `text` vid markören och returnerar nytt värde och markörens nya läge. */
export function insertAt(value: string, text: string, start?: number | null, end?: number | null): { value: string; caret: number } {
  const s = start ?? value.length;
  const e = end ?? s;
  return { value: value.slice(0, s) + text + value.slice(e), caret: s + text.length };
}

// useLayoutEffect i webbläsaren, useEffect vid serverrendering (tester).
const useIsoLayoutEffect = typeof window === 'undefined' ? React.useEffect : React.useLayoutEffect;

const fmtTime = (sec: number) => `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, '0')}`;

export type ChatComposerProps = {
  value: string;
  onChange: (v: string) => void;
  onSend: () => void;
  /** Spärrar skicka (t.ex. under uppladdning). */
  busy?: boolean;
  /** Rader i "+"-menyn. En enda rad öppnas direkt utan meny. */
  attachments?: MenuItem[];
  placeholder?: string;
  /** Mikrofonen dikterar (tal till text). Av = skicka-knappen syns hela tiden. */
  dictation?: boolean;
  lang?: 'sv' | 'en';
  style?: React.CSSProperties;
};

/** Skrivfältet: + · [text 😊] · mikrofon/skicka. Enter skickar, Skift+Enter ger ny rad. */
export function ChatComposer({ value, onChange, onSend, busy, attachments = [], placeholder = 'Skriv ett meddelande', dictation = true, lang = 'sv', style }: ChatComposerProps) {
  const T = useTheme();
  const ta = React.useRef<HTMLTextAreaElement>(null);
  const [emojiOpen, setEmojiOpen] = React.useState(false);
  const [focused, setFocused] = React.useState(false);
  const canDictate = dictation && dictationSupported();
  const valueRef = React.useRef(value);
  valueRef.current = value;
  const dict = useDictation((text) => {
    const cur = valueRef.current;
    onChange(cur.trim() ? `${cur.replace(/\s+$/, '')} ${text}` : text);
    requestAnimationFrame(() => ta.current?.focus());
  }, lang);
  const action = composerAction(value, canDictate, dict.state);

  // Fältet växer med texten upp till fem rader.
  useIsoLayoutEffect(() => {
    const el = ta.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 132)}px`;
  }, [value]);

  const send = () => { if (!busy && value.trim()) onSend(); };
  const insertEmoji = (e: string) => {
    const el = ta.current;
    const r = insertAt(value, e, el?.selectionStart, el?.selectionEnd);
    onChange(r.value);
    requestAnimationFrame(() => { el?.focus(); el?.setSelectionRange(r.caret, r.caret); });
  };

  const recording = dict.state === 'recording';
  const round: React.CSSProperties = { width: TAP, height: TAP };
  const plus = attachments.length === 1
    ? <IconButton label={String(attachments[0].label)} disabled={busy || recording} onClick={attachments[0].onSelect} style={{ color: T.text }}><Icon as={Plus} size={24} /></IconButton>
    : attachments.length > 1 && (
      <Menu placement="top" items={attachments} trigger={(p) => (
        <IconButton {...p} label="Bifoga" disabled={busy || recording} style={{ color: T.text }}><Icon as={Plus} size={24} /></IconButton>
      )} />
    );

  return (
    <div style={{ position: 'relative', ...style }}>
      {emojiOpen && (
        <EmojiPicker onPick={insertEmoji} onClose={() => { setEmojiOpen(false); ta.current?.focus(); }} />
      )}
      {(dict.error) && (
        <div role="alert" style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '0 4px 6px', padding: '6px 6px 6px 12px', borderRadius: RADIUS.md, background: T.dangerSoft, color: T.danger, fontSize: 13, fontWeight: 600 }}>
          <span style={{ flex: 1 }}>{dict.error}</span>
          <IconButton label="Stäng" onClick={dict.clearError} style={{ color: T.danger }}><Icon as={X} size={18} /></IconButton>
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6 }}>
        {recording
          ? <IconButton label="Avbryt inspelningen" onClick={dict.cancel} style={{ color: T.danger }}><Icon as={X} size={22} /></IconButton>
          : plus}
        <div style={{
          flex: 1, minWidth: 0, display: 'flex', alignItems: 'flex-end', minHeight: TAP + 2,
          background: T.surface2, border: `1px solid ${focused ? T.focus : T.border}`, borderRadius: 22,
          boxShadow: focused ? `0 0 0 1px ${T.focus}` : 'none', transition: 'border-color .15s, box-shadow .15s',
        }}>
          {recording || dict.state === 'transcribing' ? (
            <div role="status" style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 10, minHeight: TAP, padding: '0 16px', fontSize: 15, color: T.text }}>
              {recording && <span aria-hidden style={{ width: 10, height: 10, borderRadius: '50%', background: T.danger, animation: 'qPulse 1.2s ease-in-out infinite' }} />}
              <span>{recording ? `Lyssnar… ${fmtTime(dict.elapsed)}` : 'Skriver ut…'}</span>
              <style>{'@keyframes qPulse{0%,100%{opacity:1}50%{opacity:.35}}'}</style>
            </div>
          ) : (
            <>
              <textarea
                ref={ta} rows={1} value={value} placeholder={placeholder} aria-label="Meddelande"
                enterKeyHint="send"
                onChange={(e) => onChange(e.target.value)}
                onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); send(); } }}
                style={{
                  flex: 1, minWidth: 0, resize: 'none', border: 'none', outline: 'none', background: 'transparent', color: T.text,
                  fontFamily: 'inherit', fontSize: 16, lineHeight: '22px', padding: '11px 0 11px 16px', maxHeight: 132, display: 'block',
                }}
              />
              <IconButton label="Emoji" aria-expanded={emojiOpen} onClick={() => setEmojiOpen((o) => !o)} style={{ color: T.textMuted }}>
                <Icon as={Smile} size={22} />
              </IconButton>
            </>
          )}
        </div>
        <IconButton
          variant="primary" round
          label={action === 'send' ? 'Skicka' : action === 'stop' ? 'Stoppa och skriv ut' : action === 'busy' ? 'Skriver ut' : 'Diktera meddelande'}
          disabled={action === 'busy' || (action === 'send' && (busy || !value.trim()))}
          onClick={() => { if (action === 'send') send(); else if (action === 'mic') void dict.start(); else if (action === 'stop') dict.stop(); }}
          style={{ ...round, opacity: action === 'send' && !value.trim() ? 0.55 : 1, transition: 'opacity .15s' }}
        >
          {action === 'send' ? <Icon as={SendHorizontal} size={20} /> : action === 'stop' ? <Icon as={Square} size={16} /> : action === 'busy'
            ? <span aria-hidden style={{ width: 18, height: 18, borderRadius: '50%', border: '2px solid currentColor', borderTopColor: 'transparent', animation: 'spin .8s linear infinite' }} />
            : <Icon as={Mic} size={22} />}
        </IconButton>
      </div>
    </div>
  );
}

/** Ruta med emoji ovanför skrivfältet. Escape och klick utanför stänger. */
function EmojiPicker({ onPick, onClose }: { onPick: (e: string) => void; onClose: () => void }) {
  const T = useTheme();
  const ref = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    ref.current?.querySelector<HTMLElement>('button')?.focus({ preventScroll: true });
    const onDown = (e: MouseEvent | TouchEvent) => { if (!ref.current?.contains(e.target as Node) && !(e.target as HTMLElement).closest?.('[aria-label="Emoji"]')) onClose(); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('touchstart', onDown); };
  }, [onClose]);
  return (
    <div ref={ref} role="dialog" aria-label="Välj emoji" onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } }}
      style={{
        position: 'absolute', bottom: 'calc(100% + 8px)', right: 0, zIndex: 20, width: 'min(340px, 100%)',
        display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2, padding: 8,
        background: T.surface, border: `1px solid ${T.border}`, borderRadius: RADIUS.lg, boxShadow: T.shadow.float,
      }}>
      {CHAT_EMOJI.map((e) => (
        <button key={e} type="button" aria-label={e} onClick={() => onPick(e)}
          style={{ height: TAP, border: 'none', borderRadius: RADIUS.sm, background: 'transparent', fontSize: 24, cursor: 'pointer', lineHeight: 1 }}
          onMouseEnter={(ev) => { ev.currentTarget.style.background = T.surface2; }}
          onMouseLeave={(ev) => { ev.currentTarget.style.background = 'transparent'; }}>
          {e}
        </button>
      ))}
    </div>
  );
}
